import { useCallback, useMemo, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { Check, Copy, KeyRound, Lock, RefreshCw, ChevronDown, X } from 'lucide-react';

import { Button, buttonVariants } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { useExecutionStore } from '@/lib/storage/provider';
import { ONBOARDING_PROJECT_KEY } from '@/lib/first-run-keys';
import { cn } from '@/lib/utils';

const Kw = ({ children }: { children: ReactNode }): import('react/jsx-runtime').JSX.Element => (
  <span style={{ color: 'var(--flux-time)' }}>{children}</span>
);
const Fn = ({ children }: { children: ReactNode }): import('react/jsx-runtime').JSX.Element => (
  <span style={{ color: 'var(--flux-fetch)' }}>{children}</span>
);
const Str = ({ children }: { children: ReactNode }): import('react/jsx-runtime').JSX.Element => (
  <span style={{ color: 'var(--flux-math)' }}>{children}</span>
);
const Prop = ({ children }: { children: ReactNode }): import('react/jsx-runtime').JSX.Element => (
  <span style={{ color: 'var(--flux-env)' }}>{children}</span>
);

interface KeyPair {
  publicKey: string;
  privateKey: string;
}

function CopyButton({ text }: { text: string }): import('react/jsx-runtime').JSX.Element {
  const [copied, setCopied] = useState(false);
  return (
    <button
      type="button"
      onClick={(): void => {
        void navigator.clipboard.writeText(text);
        setCopied(true);
        setTimeout((): void => setCopied(false), 2000);
      }}
      className="text-muted-foreground hover:text-foreground transition-colors"
    >
      {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
    </button>
  );
}

function KeyRow({
  label,
  value,
  mono = true,
}: {
  label: string;
  value: string;
  mono?: boolean;
}): import('react/jsx-runtime').JSX.Element {
  const [copied, setCopied] = useState(false);
  return (
    <div className="flex items-center justify-between gap-2 rounded bg-[#0d1117] border border-white/10 px-3 py-2">
      <span className={`text-xs truncate ${mono ? 'font-mono' : ''} text-white/80`}>{label}</span>
      <button
        type="button"
        onClick={(): void => {
          void navigator.clipboard.writeText(value);
          setCopied(true);
          setTimeout((): void => setCopied(false), 2000);
        }}
        className="shrink-0 text-muted-foreground hover:text-foreground transition-colors"
      >
        {copied ? (
          <Check className="h-3.5 w-3.5 text-[var(--flux-success)]" />
        ) : (
          <Copy className="h-3.5 w-3.5" />
        )}
      </button>
    </div>
  );
}

export function FirstRunSetup({
  projects,
  dismissed,
  onDismiss,
}: {
  projects: { id: string; name: string }[];
  dismissed: boolean;
  onDismiss: () => void;
}): import('react/jsx-runtime').JSX.Element | null {
  const { executions, isLoading } = useExecutionStore();
  const project = useMemo((): { id: string; name: string } | null => {
    if (projects.length === 0) return null;
    if (typeof globalThis.window === 'undefined') return projects[0] ?? null;
    try {
      const raw = sessionStorage.getItem(ONBOARDING_PROJECT_KEY);
      if (raw) {
        const parsed = JSON.parse(raw) as { id?: string; name?: string };
        sessionStorage.removeItem(ONBOARDING_PROJECT_KEY);
        if (parsed.id != null) {
          const found = projects.find((p): boolean => p.id === parsed.id);
          if (found) return found;
          return { id: parsed.id, name: parsed.name ?? 'App' };
        }
      }
    } catch {
      // ignore
    }
    return projects[0] ?? null;
  }, [projects]);
  const [agentUrl, setAgentUrl] = useState('');
  const [keys, setKeys] = useState<KeyPair | null>(null);
  const [generatingKeys, setGeneratingKeys] = useState(false);
  const [advancedOpen, setAdvancedOpen] = useState(false);

  const dismiss = useCallback((): void => {
    onDismiss();
  }, [onDismiss]);

  async function saveAgentUrl(): Promise<void> {
    if (project == null || !agentUrl.trim()) return;
    await fetch(`/api/projects/${project.id}/agent`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ agentUrl: agentUrl.trim() }),
    });
  }

  async function generateKeyPair(): Promise<void> {
    setGeneratingKeys(true);
    const keyPair = await crypto.subtle.generateKey(
      {
        name: 'RSA-OAEP',
        modulusLength: 2048,
        publicExponent: new Uint8Array([1, 0, 1]),
        hash: 'SHA-256',
      },
      true,
      ['encrypt', 'decrypt'],
    );
    const toB64 = (buf: ArrayBuffer): string => btoa(String.fromCharCode(...new Uint8Array(buf)));
    const wrap = (b64: string, label: string): string =>
      `-----BEGIN ${label}-----\n${b64.match(/.{1,64}/g)!.join('\n')}\n-----END ${label}-----`;
    const pubRaw = await crypto.subtle.exportKey('spki', keyPair.publicKey);
    const privRaw = await crypto.subtle.exportKey('pkcs8', keyPair.privateKey);
    setKeys({
      publicKey: wrap(toB64(pubRaw), 'PUBLIC KEY'),
      privateKey: wrap(toB64(privRaw), 'PRIVATE KEY'),
    });
    setGeneratingKeys(false);
  }

  if (isLoading) return null;
  if (projects.length === 0) return null;

  if (executions.length > 0) {
    return (
      <div className="rounded-xl border border-[var(--flux-success)]/30 bg-[var(--flux-success)]/10 px-4 py-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
        <p className="text-sm text-foreground">
          <span className="text-[var(--flux-success)]">✓</span> Your first execution is captured.
        </p>
        <div className="flex items-center gap-2">
          <Link to="/explorer" className={buttonVariants({ variant: 'secondary', size: 'sm' })}>
            Replay it
          </Link>
        </div>
      </div>
    );
  }

  if (dismissed) return null;
  if (project == null) return null;

  const appLabel = project.name || 'my-app';

  return (
    <Card className="border-white/10 overflow-hidden">
      <CardHeader className="border-b border-white/5 flex flex-row items-start justify-between gap-4 space-y-0 pb-3">
        <div>
          <CardTitle className="text-base">Set up SDK</CardTitle>
          <p className="text-sm text-muted-foreground mt-1">
            Install, add keys, and run your app so executions show up in the feed below.
          </p>
        </div>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="shrink-0 -mr-1"
          onClick={dismiss}
          aria-label="Dismiss setup"
        >
          <X className="h-4 w-4" />
        </Button>
      </CardHeader>
      <CardContent className="pt-4 space-y-6">
        <div className="space-y-2">
          <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
            Step 1
          </p>
          <p className="text-sm">Install the SDK in your app directory.</p>
          <div className="rounded-md bg-[#0d1117] border border-white/10 p-3 font-mono text-sm flex items-center justify-between">
            <span>
              <span style={{ color: 'var(--flux-accent)' }}>$</span>{' '}
              <span className="text-white">npm install fluxrun</span>
            </span>
            <CopyButton text="npm install fluxrun" />
          </div>
        </div>

        <div className="space-y-2">
          <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
            Step 2
          </p>
          <p className="text-sm text-muted-foreground">
            Add <code className="text-foreground/80">FLUX_PROJECT_ID</code> to your environment. For{' '}
            <code className="text-foreground/80">FLUX_SECRET</code>, open project settings to copy
            the credential (never committed to git).
          </p>
          <KeyRow
            label={`FLUX_PROJECT_ID=${project.id.slice(0, 8)}…`}
            value={`FLUX_PROJECT_ID=${project.id}`}
          />
          <Link
            to={`/projects/${project.id}`}
            className={buttonVariants({ variant: 'outline', size: 'sm' })}
          >
            Open project credentials
          </Link>
        </div>

        <div>
          <button
            type="button"
            onClick={(): void => setAdvancedOpen((o): boolean => !o)}
            className="flex w-full items-center justify-between text-left text-sm font-medium text-muted-foreground py-1"
          >
            <span>Advanced (optional)</span>
            <ChevronDown className={cn('h-4 w-4 transition', advancedOpen && 'rotate-180')} />
          </button>
          {advancedOpen && (
            <div className="mt-3 space-y-4 border-t border-border pt-4">
              <div>
                <p className="text-sm mb-2 text-muted-foreground">
                  Example function wrapped with fluxRun:
                </p>
                <pre className="rounded-md bg-[#0d1117] border border-white/10 p-3 text-xs font-mono overflow-x-auto text-white max-h-48 overflow-y-auto">
                  <code className="leading-[1.8] block">
                    <div>
                      <Kw>import</Kw> {'{ '}
                      <Fn>fluxFunc</Fn>
                      {', '}
                      <Fn>fluxENV</Fn>
                      {' } '}
                      <Kw>from</Kw> <Str>&quot;fluxrun&quot;</Str>;
                    </div>
                    <div>
                      <Kw>export default</Kw> <Fn>fluxFunc</Fn>(<Kw>async function</Kw>{' '}
                      <Fn>handler</Fn>() {'{'}
                    </div>
                    <div>
                      {'  '}
                      <Kw>return</Kw> {'{ '}
                      <Prop>ok</Prop>: <Kw>true</Kw> {'};'}
                    </div>
                    <div>{'}, {'}</div>
                    <div>
                      {'  '}
                      <Prop>name</Prop>
                      {': '}
                      <Str>{`"${appLabel}"`}</Str>
                      {', '}
                    </div>
                    <div>{'});'}</div>
                  </code>
                </pre>
              </div>

              <div className="grid sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <div className="flex items-center gap-1.5">
                    <Lock className="h-3.5 w-3.5 text-[var(--flux-crypto)]" />
                    <span className="text-xs font-medium">Replay encryption (optional)</span>
                  </div>
                  {!keys ? (
                    <Button
                      variant="outline"
                      size="sm"
                      className="w-full"
                      onClick={generateKeyPair}
                      disabled={generatingKeys}
                    >
                      {generatingKeys ? (
                        <>
                          <RefreshCw className="h-3.5 w-3.5 mr-2 animate-spin" />
                          Generating…
                        </>
                      ) : (
                        <>
                          <KeyRound className="h-3.5 w-3.5 mr-2" />
                          Generate key pair
                        </>
                      )}
                    </Button>
                  ) : (
                    <div className="space-y-2 text-[10px] font-mono text-white/60 break-all max-h-24 overflow-y-auto">
                      {keys.publicKey.slice(0, 200)}…
                    </div>
                  )}
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="fr-agent" className="text-xs">
                    Agent URL (optional)
                  </Label>
                  <Input
                    id="fr-agent"
                    placeholder="https://myapp.com/api/flux-agent"
                    value={agentUrl}
                    onChange={(e): void => setAgentUrl(e.target.value)}
                  />
                  <Button type="button" size="sm" variant="secondary" onClick={saveAgentUrl}>
                    Save
                  </Button>
                </div>
              </div>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
